import React, { useEffect, useState } from 'react';
import { Card, Typography, Button, Empty, Tag, Select, Input, message, Avatar } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { CardListSkeleton } from '../../../components/SkeletonLoaders';
import { employeesApi } from '../../../utils/api';
import { actionApi } from '../../../utils/actionApi';
import PersianDatePicker from '../../../components/PersianDatePicker';

const { Title, Text } = Typography;
const { TextArea } = Input;

export default function ActionRequestsTab({
  actionId,
  requests,
  requestsLoading,
  loadRequests,
  requestType,
  setRequestType,
  requestTitle,
  setRequestTitle,
  requestDescription,
  setRequestDescription,
  requestRequestedTo,
  setRequestRequestedTo,
  requestActionItemId,
  setRequestActionItemId,
  requestDeadline,
  setRequestDeadline,
  requestNewUserId,
  setRequestNewUserId,
  requestSubmitting,
  onCreateRequest,
  primaryButtonClass,
  setRequestedToActionId,
  setNewUserActionId,
  requestedToActionId,
  newUserActionId,
}) {
  const [erpEmployees, setErpEmployees] = useState([]);
  const [erpLoading, setErpLoading] = useState(false);
  const [actionItems, setActionItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [syncingRequestedTo, setSyncingRequestedTo] = useState(false);
  const [syncingNewUser, setSyncingNewUser] = useState(false);

  const fetchErpEmployees = async (search = '') => {
    setErpLoading(true);
    try {
      const filters = {};
      if (search) {
        filters.name = search;
      }
      const res = await employeesApi.getAll(filters);
      const list = (res.data || []).map((item) => ({
        value: String(item.id),
        label: `${item.name || 'بدون نام'}${item.email ? ` - ${item.email}` : ''}`,
      }));
      setErpEmployees(list);
    } catch (e) {
      console.error(e);
      message.error('خطا در دریافت لیست کارمندان');
    } finally {
      setErpLoading(false);
    }
  };

  const fetchActionItems = async () => {
    if (!actionId) return;
    setItemsLoading(true);
    try {
      const res = await actionApi.getActionItems(actionId);
      // ساختار پاسخ: { data: [...] } - data خودش یک array است
      let list = [];
      if (Array.isArray(res?.data)) {
        list = res.data;
      } else if (Array.isArray(res?.data?.data)) {
        list = res.data.data;
      }
      setActionItems(list);
    } catch (e) {
      console.error(e);
      message.error('خطا در دریافت لیست آیتم‌های اقدام');
    } finally {
      setItemsLoading(false);
    }
  };

  useEffect(() => {
    // بارگذاری اولیه لیست کارمندان برای requested_to
    fetchErpEmployees();
    // بارگذاری لیست آیتم‌های اقدام
    if (actionId) {
      fetchActionItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionId]);

  useEffect(() => {
    // اگر نوع درخواست تغییر کرد، لیست کارمندان را دوباره بارگذاری کن
    if (requestType === 'انتقال_مسئولیت' || requestType) {
      fetchErpEmployees();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestType]);

  // همگام‌سازی کاربر requested_to
  const handleRequestedToChange = async (erpUserId) => {
    if (!erpUserId) {
      setRequestRequestedTo('');
      if (setRequestedToActionId) {
        setRequestedToActionId(null);
      }
      return;
    }

    setRequestRequestedTo(erpUserId);
    setSyncingRequestedTo(true);
    try {
      const syncRes = await actionApi.syncUserFromErp(erpUserId);
      if (!syncRes?.success || !syncRes.data?.id) {
        throw new Error(syncRes?.message || 'خطا در همگام‌سازی کاربر مقصد');
      }
      const actionId = syncRes.data.id;
      if (setRequestedToActionId) {
        setRequestedToActionId(actionId);
      }
      message.success('کاربر با موفقیت همگام‌سازی شد');
    } catch (e) {
      console.error(e);
      message.error(e.message || 'خطا در همگام‌سازی کاربر مقصد');
      setRequestRequestedTo('');
      if (setRequestedToActionId) {
        setRequestedToActionId(null);
      }
    } finally {
      setSyncingRequestedTo(false);
    }
  };

  // همگام‌سازی کاربر جدید برای انتقال مسئولیت
  const handleNewUserChange = async (erpUserId) => {
    if (!erpUserId) {
      setRequestNewUserId('');
      if (setNewUserActionId) {
        setNewUserActionId(null);
      }
      return;
    }

    setRequestNewUserId(erpUserId);
    setSyncingNewUser(true);
    try {
      const syncRes = await actionApi.syncUserFromErp(erpUserId);
      if (!syncRes?.success || !syncRes.data?.id) {
        throw new Error(syncRes?.message || 'خطا در همگام‌سازی کاربر جدید');
      }
      const actionId = syncRes.data.id;
      if (setNewUserActionId) {
        setNewUserActionId(actionId);
      }
      message.success('کاربر با موفقیت همگام‌سازی شد');
    } catch (e) {
      console.error(e);
      message.error(e.message || 'خطا در همگام‌سازی کاربر جدید');
      setRequestNewUserId('');
      if (setNewUserActionId) {
        setNewUserActionId(null);
      }
    } finally {
      setSyncingNewUser(false);
    }
  };
  return (
    <Card style={{ borderRadius: 16 }} bodyStyle={{ padding: 16 }}>
      <div>
        <Title level={5} style={{ margin: 0, fontFamily: 'CustomFont' }}>
          درخواست‌های مرتبط با اقدام
        </Title>
        <Text type="secondary" style={{ fontFamily: 'CustomFont' }}>
          ایجاد و مشاهده درخواست‌هایی مثل تمدید ددلاین، منابع بیشتر و انتقال مسئولیت
        </Text>
      </div>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          {requestsLoading ? (
            <CardListSkeleton count={3} />
          ) : requests.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span style={{ fontFamily: 'CustomFont' }}>
                  هنوز درخواستی ثبت نشده است
                </span>
              }
            />
          ) : (
            <div className="space-y-3">
              {requests.map((r) => {
                const getStatusColor = (status) => {
                  if (!status) return 'default';
                  const statusStr = String(status).toLowerCase();
                  if (statusStr.includes('در_انتظار') || statusStr === 'pending' || statusStr === 'در انتظار') return 'gold';
                  if (statusStr.includes('تایید') || statusStr === 'approved' || statusStr === 'تایید شده') return 'green';
                  if (statusStr.includes('رد') || statusStr === 'rejected' || statusStr === 'رد شده') return 'red';
                  return 'default';
                };

                const formatDate = (dateString) => {
                  if (!dateString) return '';
                  try {
                    const date = new Date(dateString);
                    return date.toLocaleDateString('fa-IR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    });
                  } catch {
                    return dateString;
                  }
                };

                return (
                  <div
                    key={r.id}
                    className="p-4 rounded-xl border border-gray-200 dark:border-gray-800 flex flex-col gap-3 hover:shadow-md transition-shadow bg-white dark:bg-gray-800"
                  >
                    {/* هدر */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Tag color="geekblue" style={{ fontFamily: 'CustomFont', margin: 0 }}>
                            {r.request_type_label || r.request_type}
                          </Tag>
                          <Text strong style={{ fontFamily: 'CustomFont', fontSize: 15 }}>
                            {r.title}
                          </Text>
                        </div>
                        {r.description && (
                          <Text type="secondary" style={{ fontFamily: 'CustomFont', fontSize: 13, display: 'block' }}>
                            {r.description}
                          </Text>
                        )}
                      </div>
                      {r.status && (
                        <Tag
                          color={getStatusColor(r.status)}
                          style={{ fontFamily: 'CustomFont', margin: 0 }}
                        >
                          {r.status_label || r.status}
                        </Tag>
                      )}
                    </div>

                    {/* اطلاعات درخواست */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-gray-100 dark:border-gray-700">
                      {/* درخواست‌دهنده */}
                      {r.requested_by && (
                        <div>
                          <Text type="secondary" style={{ fontFamily: 'CustomFont', fontSize: 11, display: 'block', marginBottom: 4 }}>
                            درخواست‌دهنده:
                          </Text>
                          <Text style={{ fontFamily: 'CustomFont', fontSize: 13 }}>
                            {r.requested_by.name}
                          </Text>
                          {r.requested_by.email && (
                            <Text type="secondary" style={{ fontFamily: 'CustomFont', fontSize: 12, display: 'block' }}>
                              {r.requested_by.email}
                            </Text>
                          )}
                        </div>
                      )}

                      {/* درخواست‌شونده */}
                      {r.requested_to && (
                        <div>
                          <Text type="secondary" style={{ fontFamily: 'CustomFont', fontSize: 11, display: 'block', marginBottom: 4 }}>
                            درخواست‌شونده:
                          </Text>
                          <Text style={{ fontFamily: 'CustomFont', fontSize: 13 }}>
                            {r.requested_to.name}
                          </Text>
                          {r.requested_to.email && (
                            <Text type="secondary" style={{ fontFamily: 'CustomFont', fontSize: 12, display: 'block' }}>
                              {r.requested_to.email}
                            </Text>
                          )}
                        </div>
                      )}

                      {/* آیتم اقدام */}
                      {r.action_item && (
                        <div>
                          <Text type="secondary" style={{ fontFamily: 'CustomFont', fontSize: 11, display: 'block', marginBottom: 4 }}>
                            آیتم اقدام:
                          </Text>
                          <Text style={{ fontFamily: 'CustomFont', fontSize: 13 }}>
                            {r.action_item.title} (ID: {r.action_item.id})
                          </Text>
                        </div>
                      )}

                      {/* تاریخ ایجاد */}
                      {r.created_at && (
                        <div>
                          <Text type="secondary" style={{ fontFamily: 'CustomFont', fontSize: 11, display: 'block', marginBottom: 4 }}>
                            تاریخ ایجاد:
                          </Text>
                          <Text style={{ fontFamily: 'CustomFont', fontSize: 13 }}>
                            {formatDate(r.created_at)}
                          </Text>
                        </div>
                      )}
                    </div>

                    {/* Metadata برای انتقال مسئولیت */}
                    {r.request_type === 'انتقال_مسئولیت' && r.metadata?.new_user_id && (
                      <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
                        <Text type="secondary" style={{ fontFamily: 'CustomFont', fontSize: 11, display: 'block', marginBottom: 4 }}>
                          کاربر جدید:
                        </Text>
                        <Text style={{ fontFamily: 'CustomFont', fontSize: 13 }}>
                          شناسه کاربر: {r.metadata.new_user_id}
                        </Text>
                      </div>
                    )}

                    {/* Metadata برای تمدید ددلاین */}
                    {r.request_type === 'تمدید_ددلاین' && r.metadata?.new_deadline && (
                      <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
                        <Text type="secondary" style={{ fontFamily: 'CustomFont', fontSize: 11, display: 'block', marginBottom: 4 }}>
                          ددلاین جدید:
                        </Text>
                        <Text style={{ fontFamily: 'CustomFont', fontSize: 13 }}>
                          {formatDate(r.metadata.new_deadline)}
                        </Text>
                      </div>
                    )}

                    {/* پاسخ */}
                    {r.response_comment && (
                      <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
                        <Text type="secondary" style={{ fontFamily: 'CustomFont', fontSize: 11, display: 'block', marginBottom: 4 }}>
                          پاسخ:
                        </Text>
                        <Text style={{ fontFamily: 'CustomFont', fontSize: 13 }}>
                          {r.response_comment}
                        </Text>
                        {r.responded_by && (
                          <Text type="secondary" style={{ fontFamily: 'CustomFont', fontSize: 12, display: 'block', marginTop: 4 }}>
                            توسط: {r.responded_by.name}
                            {r.responded_at && ` در ${formatDate(r.responded_at)}`}
                          </Text>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="lg:col-span-1">
          <Card
            size="small"
            style={{ borderRadius: 16, background: '#f9fafb' }}
            bodyStyle={{ padding: 12 }}
          >
            <Title level={5} style={{ margin: 0, fontFamily: 'CustomFont' }}>
              ایجاد درخواست جدید
            </Title>
            <Text type="secondary" style={{ fontFamily: 'CustomFont', fontSize: 12 }}>
              لطفاً نوع درخواست و جزئیات را مشخص کنید.
            </Text>

            <div className="mt-3 space-y-2">
              <Text style={{ fontFamily: 'CustomFont' }} strong>
                نوع درخواست
              </Text>
              <Select
                size="middle"
                placeholder="انتخاب نوع"
                value={requestType}
                onChange={setRequestType}
                style={{ width: '100%' }}
                options={[
                  { value: 'تمدید_ددلاین', label: 'تمدید ددلاین' },
                  { value: 'منابع_بیشتر', label: 'منابع بیشتر' },
                  { value: 'انتقال_مسئولیت', label: 'انتقال مسئولیت' },
                  { value: 'کمک', label: 'درخواست کمک' },
                ]}
              />

              <div>
                <Text style={{ fontFamily: 'CustomFont' }} strong>
                  عنوان درخواست
                </Text>
                <Input
                  maxLength={255}
                  value={requestTitle}
                  onChange={(e) => setRequestTitle(e.target.value)}
                  style={{ marginTop: 4, borderRadius: 10 }}
                />
              </div>

              <div>
                <Text style={{ fontFamily: 'CustomFont' }}>
                  توضیحات (اختیاری، حداکثر ۲۰۰۰ کاراکتر)
                </Text>
                <TextArea
                  rows={3}
                  maxLength={2000}
                  value={requestDescription}
                  onChange={(e) => setRequestDescription(e.target.value)}
                  style={{ marginTop: 4, borderRadius: 10 }}
                />
              </div>

              <div>
                <Text style={{ fontFamily: 'CustomFont', fontSize: 12 }}>
                  آیتم اقدام (اختیاری)
                </Text>
                <Select
                  size="middle"
                  placeholder="انتخاب آیتم اقدام"
                  value={requestActionItemId || undefined}
                  onChange={(val) => setRequestActionItemId(val)}
                  allowClear
                  loading={itemsLoading}
                  style={{ width: '100%', marginTop: 4 }}
                  options={actionItems.map((item) => ({
                    value: String(item.id),
                    label: `${item.id} - ${item.title || 'بدون عنوان'}`,
                  }))}
                />
              </div>

              <div>
                <Text style={{ fontFamily: 'CustomFont', fontSize: 12 }}>
                  کاربر مقصد (اختیاری)
                </Text>
                <Select
                  showSearch
                  size="middle"
                  placeholder="جستجو و انتخاب کاربر از ERP"
                  value={requestRequestedTo || undefined}
                  onSearch={(val) => fetchErpEmployees(val)}
                  onChange={(val) => handleRequestedToChange(val)}
                  loading={erpLoading || syncingRequestedTo}
                  filterOption={false}
                  allowClear
                  style={{ width: '100%', marginTop: 4 }}
                  options={erpEmployees}
                />
              </div>

              {requestType === 'تمدید_ددلاین' && (
                <div>
                  <Text style={{ fontFamily: 'CustomFont' }} strong>
                    ددلاین جدید (الزامی برای تمدید ددلاین)
                  </Text>
                  <PersianDatePicker
                    value={requestDeadline}
                    onChange={(val) => setRequestDeadline(val)}
                    placeholder="انتخاب تاریخ و ساعت ددلاین جدید"
                    showTime={true}
                    style={{ width: '100%', marginTop: 4 }}
                  />
                </div>
              )}

              {requestType === 'انتقال_مسئولیت' && (
                <div>
                  <Text style={{ fontFamily: 'CustomFont' }}>
                    انتخاب کاربر جدید (الزامی برای انتقال مسئولیت)
                  </Text>
                  <Select
                    showSearch
                    size="middle"
                    placeholder="جستجو و انتخاب کاربر از ERP"
                    value={requestNewUserId || undefined}
                    onSearch={(val) => fetchErpEmployees(val)}
                    onChange={(val) => handleNewUserChange(val)}
                    loading={erpLoading || syncingNewUser}
                    filterOption={false}
                    allowClear
                    style={{ width: '100%', marginTop: 4 }}
                    options={erpEmployees}
                  />
                </div>
              )}
            </div>

            <div className="mt-3 flex justify-end">
              <Button
                type="primary"
                className={primaryButtonClass}
                loading={requestSubmitting}
                disabled={
                  requestSubmitting ||
                  syncingRequestedTo ||
                  syncingNewUser ||
                  (requestType === 'انتقال_مسئولیت' && (!newUserActionId || syncingNewUser)) ||
                  (requestRequestedTo && (!requestedToActionId || syncingRequestedTo))
                }
                style={{ borderRadius: 12, fontFamily: 'CustomFont' }}
                onClick={onCreateRequest}
              >
                ثبت درخواست
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </Card>
  );
}
